import qrcode from 'qrcode-generator';
import { h } from '../lib/dom.js';
import { getState, CONFIG } from '../store.js';
import { iconMarkup } from '../icons/index.js';
import {
  setReceiveAccount, setReceiveCcy, setReceiveChain,
  copyText, ensureReceiveAddress, toggleReceiveDetails
} from '../actions.js';

// Generated fresh at render time from the (worker-derived) address string —
// never cached in state, since it's a pure, cheap synchronous function of
// that string. createDataURL() returns a data: URL image, not a markup
// string, so this never goes anywhere near innerHTML/the html: attr (that
// path is reserved for our own static icon SVGs — CLAUDE.md forbids
// injecting network/worker-derived data through it). Fixed type-number 0
// (auto-size) and 'M' error correction — reasonable defaults for a short
// address string.
function qrDataUrl(text) {
  const qr = qrcode(0, 'M');
  qr.addData(text);
  qr.make();
  return qr.createDataURL(6, 8);
}

// ensureReceiveAddress is triggered from the field-change actions
// (setReceiveAccount/Ccy/Chain) and goReceive — never from render itself,
// which would cause reentrant renders (see the comment in actions.js).
export function renderReceive() {
  const s = getState();
  const r = s.receive;
  const wallet = s.wallets.find((w) => w.id === r.accountId) || s.wallets[0];

  const ccyOptions = Object.keys(wallet.balances);
  if (!ccyOptions.includes(s.reportingCurrency)) ccyOptions.push(s.reportingCurrency);
  const isCrypto = r.ccy !== s.reportingCurrency;
  const chainOptions = isCrypto ? (CONFIG.CHAINS[r.ccy] || []) : [];

  const accountSelect = h('select', { onchange: (e) => setReceiveAccount(e.target.value) },
    s.wallets.map((w) => h('option', { value: w.id, selected: w.id === r.accountId || undefined }, w.name)));

  const ccySelect = h('select', { onchange: (e) => setReceiveCcy(e.target.value) },
    ccyOptions.map((c) => h('option', { value: c, selected: c === r.ccy || undefined }, c)));

  const detailsToggle = h('button', { class: 'btn btn-secondary btn-sm full-width', onclick: toggleReceiveDetails },
    r.showDetails ? 'Hide account details' : 'Show account details');

  let cryptoCard = null;
  if (r.showDetails) {
    if (isCrypto) {
      const chainSelect = h('select', { onchange: (e) => { setReceiveChain(e.target.value); } },
        chainOptions.map((c) => h('option', { value: c, selected: c === (r.chain || chainOptions[0]) || undefined }, c)));

      let addressArea;
      if (r.addressLoading) {
        addressArea = h('div', { class: 'address-loading' }, [
          h('div', { class: 'spinner' }),
          h('div', { class: 'text-muted' }, 'Generating address…')
        ]);
      } else if (r.addressError) {
        addressArea = h('div', { class: 'stack items-center' }, [
          h('div', { class: 'field-error' }, r.addressError),
          h('button', { class: 'btn btn-secondary btn-sm', onclick: ensureReceiveAddress }, 'Retry')
        ]);
      } else {
        const qrImage = h('div', { class: 'qr-image-wrap' }, [
          h('img', { class: 'qr-image', src: qrDataUrl(r.address || ''), alt: 'QR code for receiving address ' + (r.address || '') })
        ]);
        addressArea = h('div', { class: 'stack items-center' }, [
          qrImage,
          h('div', { class: 'flex-gap-sm items-center' }, [
            h('div', { class: 'mono receive-address' }, r.address || ' '),
            h('button', {
              class: 'icon-btn-plain', 'aria-label': 'Copy address', onclick: () => copyText(r.address || '', 'Address', 'address')
            }, s.copyFlash === 'address'
              ? h('span', { html: iconMarkup('check', { size: 16 }) })
              : h('span', { html: iconMarkup('clipboardCopy', { size: 16 }) }))
          ])
        ]);
      }

      cryptoCard = h('div', { class: 'card card-pad-lg stack items-center' }, [
        h('div', { class: 'full-width' }, [
          h('div', { class: 'card-label' }, 'Network'),
          chainSelect
        ]),
        addressArea
      ]);
    } else {
      cryptoCard = h('div', { class: 'empty-state' },
        `${s.reportingCurrency} is your reporting currency and has no receiving address. Select a stablecoin above to generate one.`);
    }
  }

  return h('div', { class: 'screen' }, [
    h('h1', { class: 'screen-title' }, 'Receive'),
    h('div', {}, [
      h('div', { class: 'card-label' }, 'Receiving account'),
      accountSelect
    ]),
    h('div', {}, [
      h('div', { class: 'card-label' }, 'Receiving currency'),
      ccySelect
    ]),
    detailsToggle,
    cryptoCard
  ]);
}
